import { RecognitionStatus } from "constants/deferredRecognition";
import { REVENUE_DATA_SOURCE_TYPES } from "constants/revenueDataSource";
import { ProductAndServices } from "types/Models/productAndServices";
import { emptyApi } from "./emptyApi";

export type ExpenseSchedule = {
  period: string;
  period_start: string;
  period_end: string;
  booked_expense: {
    booked_from_this_period_sum: number;
    booked_bill_ids: string[];
  };
  recognized_expense: {
    recognized_from_this_month: number;
    recognized_from_previous_months: number;
    recognized_as_of_period_end: number;
    total_recognized: number;
    recognized_bill_ids_as_of_current_period: string[];
  };
  deferred_expense: {
    deferred_from_this_month: number;
    deferred_from_previous_months: number;
    prev_deferred_and_recognized_in_current_month: number;
    deferred_expense_as_of_period_end: number;
    total_deferred: number;
    deferred_bill_ids_as_of_current_period: string[];
  };
  periodic_recognized_expense: Array<{
    period: string;
    period_start: string;
    period_end: string;
    recognized_expense: number;
    deferred_expense: number;
    recognized_bill_ids: string[];
    deferred_bill_ids: string[];
  }>;
};

// Expense Schedule Details Types
export type BillDetailsResponse = {
  item_name: string;
  vendor_id: string;
  vendor_name: string;
  amount: number;
  bill_id: string;
  bill_number: string | null;
  booked_amount: number;
  bill_date: string;
  item_id: string;
  expense_schedule: {
    period: string;
    period_start: string;
    period_end: string;
    amount: number;
    status: RecognitionStatus;
  }[];
}[];

export type VendorDetailsResponse = {
  vendor_id: string;
  merchant_id: string;
  vendor_name: string;
  total_amount: number;
  bill_items_count: number;
}[];

export type VendorExpenseScheduleDetailsParams = {
  entityId: string;
  // bill_ids: string;
  vendor_ids?: string;
  product_ids?: string;
  recognition_type?: RecognitionStatus;
  bill_date_start?: string;
  bill_date_end?: string;
  recognition_date_start?: string;
  recognition_date_end?: string;
};

export type BillExpenseScheduleDetailsParams = {
  entityId: string;
  vendor_ids?: string;
  product_ids?: string;
  recognition_type?: RecognitionStatus;
  bill_date_start?: string;
  bill_date_end?: string;
  recognition_date_start?: string;
  recognition_date_end?: string;
};

export const billingApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getExpenseSchedule: build.query<
      {
        schedule: ExpenseSchedule[];
        total_booked_expense: number;
        total_recognized_expense: number;
        total_deferred_expense: number;
        total_deferred_expense_as_of_end_date: number;
        document_id?: string;
      },
      {
        entityId: string;
        start_date: string;
        end_date: string;
        carry_forward_bills: boolean;
        report_type?: string;
        generate_report?: boolean;
        export_to_mail?: boolean;
        include_deferrals?: boolean;
        source?: REVENUE_DATA_SOURCE_TYPES[];
      }
    >({
      query: ({ entityId, ...params }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/expense_schedule/`,
        params,
      }),
    }),

    getExpenseScheduleVendorDetails: build.query<
      VendorDetailsResponse,
      VendorExpenseScheduleDetailsParams
    >({
      query: ({ entityId, ...params }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/expense_schedule/details/`,
        params: { ...params, expense_detail_view: "VENDOR" },
      }),
    }),

    getExpenseScheduleBillDetails: build.query<
      BillDetailsResponse,
      BillExpenseScheduleDetailsParams
    >({
      query: ({ entityId, ...params }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/expense_schedule/details/`,
        params: { ...params, expense_detail_view: "BILL" },
      }),
    }),
  }),
});

export const {
  useGetExpenseScheduleQuery,
  useLazyGetExpenseScheduleQuery,
  useGetExpenseScheduleVendorDetailsQuery,
  useGetExpenseScheduleBillDetailsQuery,
} = billingApis;
